import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { SiteFooter } from "../components/SiteFooter";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { QrCode, Ticket, CheckCircle2, Loader2, Car, MapPin, ArrowRight } from "lucide-react";

export default function Wash() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (user && user.role === "customer") api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    if (user && user.role === "admin") navigate("/admin");
  }, [user, navigate]);

  const getTicket = async (v) => {
    setBusy(v.id);
    try {
      const res = await api.post("/wash/ticket", { vehicle_id: v.id });
      setTicket(res.data);
      toast.success("Wash ticket ready");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-volt" /></div>;

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <div className="mx-auto max-w-2xl px-6 py-12">
        <div className="text-center mb-10">
          <div className="inline-flex w-14 h-14 border border-volt items-center justify-center mb-4"><QrCode className="w-7 h-7 text-volt" /></div>
          <div className="overline text-volt mb-2 flex items-center justify-center gap-2"><MapPin className="w-4 h-4" /> 200 Homer St, Earlwood</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight">Wash Check-In</h1>
        </div>

        {!user && (
          <div className="border border-white/10 bg-surface p-8 text-center">
            <Car className="w-10 h-10 text-volt mx-auto mb-4" />
            <p className="text-muted-foreground mb-6 tracking-wide">Sign in to confirm your number plate and grab a wash ticket.</p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/login" data-testid="wash-login-btn" className="bg-volt text-white px-6 py-3 font-head font-bold uppercase tracking-wide hover:bg-blue-600 transition-colors">Sign In</Link>
              <Link to="/register" data-testid="wash-register-btn" className="border border-white/15 px-6 py-3 overline hover:border-volt hover:text-white transition-colors">Join Now</Link>
            </div>
          </div>
        )}

        {user && ticket && (
          <div data-testid="wash-ticket-result" className="border-2 border-volt bg-surface p-8 text-center reveal">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <div className="overline text-muted-foreground mb-2">Show This At The Bay</div>
            <div className="font-head font-black text-6xl tracking-tight text-volt mb-4">{ticket.code}</div>
            <div className="inline-flex items-center bg-white text-black plate font-bold text-2xl px-3 py-1 border-2 border-black">{ticket.plate}</div>
            <p className="text-sm text-muted-foreground mt-6">Issued {new Date(ticket.issued_at).toLocaleString("en-AU")}</p>
            <button data-testid="wash-another-btn" onClick={() => setTicket(null)} className="mt-6 overline text-volt hover:underline">Get another ticket</button>
          </div>
        )}

        {user && !ticket && (
          <div>
            <div className="overline text-muted-foreground mb-4 flex items-center gap-2"><Ticket className="w-4 h-4 text-volt" /> Select Your Car</div>
            <div className="space-y-3" data-testid="wash-vehicle-list">
              {vehicles.length === 0 && (
                <div className="border border-dashed border-white/15 p-8 text-center text-muted-foreground text-sm">
                  No cars on your account. <Link to="/dashboard" className="text-volt hover:underline">Add one</Link> first.
                </div>
              )}
              {vehicles.map((v) => (
                <div key={v.id} className="border border-white/10 bg-surface p-5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="inline-flex items-center bg-white text-black plate font-bold text-xl px-3 py-1 border-2 border-black">{v.plate}</div>
                    {!v.active && <span className="overline text-amber-400">No active membership</span>}
                  </div>
                  {v.active ? (
                    <button data-testid={`wash-get-${v.plate}`} onClick={() => getTicket(v)} disabled={busy === v.id}
                      className="flex items-center gap-2 bg-volt text-white px-5 py-2.5 overline hover:bg-blue-600 transition-colors disabled:opacity-60">
                      {busy === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />} Get Ticket
                    </button>
                  ) : (
                    <Link to="/dashboard" data-testid={`wash-subscribe-${v.plate}`} className="flex items-center gap-2 border border-white/15 px-5 py-2.5 overline hover:border-volt hover:text-white transition-colors">
                      Subscribe <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
